var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { filterInPlace } from './ObjectUtils';
// Examples:
//
// const timeouts = new Timeouts();
//
// await timeouts.delay(1000);
//
// timeouts.set(() => console.log('done'), 1000);
//
// const timeout = timeouts.set(() => console.log('done', 1000));
//
// const timeout = timeouts.set(() => console.log('done'), 10000);
// ...
// timeout.restart(5000);
// ...
// timeout.clear();
//
// The timeout clears itself when it expires. The clear method is provided for you to stop the
// timeout before it expires. Non-persistent timeouts are destroyed when they are cleared (i.e.,
// you can't set them again).
//
// const interval = timeouts.setInterval(() => console.log('tick'), 1000);
// ...
// interval.restart(2000);
// ...
// interval.clear();
//
// const timeout = timeouts.create();
// timeout.set(() => console.log('done'), 10000);
// ...
// timeout.restart(5000);
// ...
// timeout.clear();
// ...
// timeout.set(() => console.log('foo'), 1000);
// ...
// timeout.destroy();
//
// Persistent timeouts are not destroyed when they expire (or are cleared). They can be resused.
// They can be explicitly destroyed if you no longer need them.
let uuid = 1;
export class TimeoutError extends Error {
    constructor(message, code) {
        super(message);
        this.code = code;
        this.component = 'Timeout';
    }
}
// Persistent timeouts stay in the timeouts queue and can be rescheduled even after they expire.
// Non-persistent timeouts are destroyed as soon as they expire.
// Interval timeouts are continue rescheduling themselves. Clearing them stops them from
// rescheduling themselves and destroys them if they are non-persistent. Setting them changes their
// callback and duration.
class TimeoutImpl {
    constructor(durationMs, onCleanup, _isPersistent, _isInterval, onTimeout) {
        this.onCleanup = onCleanup;
        this._isPersistent = _isPersistent;
        this._isInterval = _isInterval;
        this._durationMs = 0;
        this._expires = 0;
        this._isRunning = false;
        this._started = 0;
        this._uuid = uuid++;
        this._isDestroyed = false;
        this.go(durationMs, onTimeout);
    }
    get durationMs() {
        return this._durationMs;
    }
    get expires() {
        return this._expires;
    }
    get id() {
        return this._uuid;
    }
    get isDestroyed() {
        return this._isDestroyed;
    }
    get isInterval() {
        return this._isInterval;
    }
    get isPersistent() {
        return this._isPersistent;
    }
    get isRunning() {
        return this._isRunning;
    }
    get remainingMs() {
        return this._expires - Date.now();
    }
    get started() {
        return this._started;
    }
    go(durationMs, onTimeout) {
        if (this._isDestroyed)
            throw new Error('timeout is destroyed');
        if (this._isRunning)
            throw new Error('timeout already running');
        if (durationMs <= 0) {
            if (!this._isPersistent) {
                this.onCleanup(false);
                this._isDestroyed = true;
            }
            return;
        }
        this._durationMs = durationMs;
        this._started = Date.now();
        this._expires = this.started + durationMs;
        this._isRunning = true;
        this._onTimeout = onTimeout;
        if (this._isInterval) {
            this._id = setInterval(() => {
                onTimeout === null || onTimeout === void 0 ? void 0 : onTimeout();
            }, durationMs);
        }
        else {
            this._id = setTimeout(() => {
                this._isRunning = false;
                if (!this._isPersistent) {
                    this.onCleanup(true);
                    this._isDestroyed = true;
                }
                onTimeout === null || onTimeout === void 0 ? void 0 : onTimeout();
            }, durationMs);
        }
    }
    restart(durationMs = 1) {
        if (this._onTimeout === undefined)
            throw new Error('onTimeout undefined');
        this.set(this._onTimeout, durationMs);
    }
    set(onTimeout, durationMs = 1) {
        if (this._isDestroyed)
            throw new Error('timeout is destroyed');
        if (this._isInterval) {
            if (this._isRunning && this._id !== undefined)
                clearInterval(this._id);
        }
        else if (this._isPersistent) {
            if (this._isRunning && this._id !== undefined)
                clearTimeout(this._id);
        }
        else {
            throw new Error("can't reschedule non-persistent, non-interval timeout");
        }
        this._isRunning = false;
        this.go(durationMs, onTimeout);
    }
    setOnTimeout(value) {
        this._onTimeout = value;
    }
    clear() {
        if (this._isDestroyed)
            return;
        if (this._isRunning && this._id !== undefined) {
            if (this._isInterval) {
                clearInterval(this._id);
            }
            else {
                clearTimeout(this._id);
            }
        }
        this._isRunning = false;
        if (!this._isPersistent) {
            this.onCleanup(false);
            this._isDestroyed = true;
        }
    }
    destroy() {
        if (this._isDestroyed)
            return;
        this.clear();
        if (this._isPersistent) {
            this.onCleanup(false);
            this._isDestroyed = true;
        }
    }
}
export class Timeouts {
    constructor() {
        this.timeouts = [];
    }
    clearAll(exceptions = []) {
        for (const timeout of [...this.timeouts]) {
            // iterate over a copy so persistent timeouts can be removed safely
            if (!exceptions.includes(timeout))
                timeout.clear();
        }
    }
    // Create a persistent timeout, but don't schedule it yet.
    create() {
        const timeout = new TimeoutImpl(0, () => this.remove(timeout), true, false);
        this.timeouts.push(timeout);
        return timeout;
    }
    createInterval() {
        const timeout = new TimeoutImpl(0, () => this.remove(timeout), true, true);
        this.timeouts.push(timeout);
        return timeout;
    }
    delay(durationMs = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            if (durationMs <= 0)
                return;
            return new Promise((resolve, reject) => {
                const timeout = new TimeoutImpl(durationMs, (expired) => {
                    this.remove(timeout);
                    if (!expired)
                        reject(new TimeoutError('timeout cleared', 'timeoutCleared'));
                }, false, false, resolve);
                this.timeouts.push(timeout);
            });
        });
    }
    log(log) {
        log.debug('timeouts:');
        for (const timeout of this.timeouts) {
            log.debug(`    timeout: [${timeout.id}]: ` +
                `per = ${timeout.isPersistent}, ` +
                `run = ${timeout.isRunning}, ` +
                `des = ${timeout.isDestroyed}, ` +
                `int = ${timeout.isInterval}, ` +
                `dur = ${timeout.durationMs}, ` +
                `exp = ${timeout.isRunning ? timeout.expires - Date.now() : 'N/A'}`);
        }
    }
    remove(timeout) {
        filterInPlace(this.timeouts, (t) => {
            return t.id !== timeout.id;
        });
    }
    set(callback, durationMs = 1, persistent = false) {
        const timeout = new TimeoutImpl(durationMs, () => this.remove(timeout), persistent, false, callback);
        this.timeouts.push(timeout);
        return timeout;
    }
    setInterval(callback, durationMs = 1, persistent = false) {
        const timeout = new TimeoutImpl(durationMs, () => this.remove(timeout), persistent, true, callback);
        this.timeouts.push(timeout);
        return timeout;
    }
}
export class GlobalTimeouts {
    constructor() {
        this.timeouts = new Timeouts();
    }
    create() {
        return this.timeouts.create();
    }
    createInterval() {
        return this.timeouts.createInterval();
    }
    delay(durationMs = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.timeouts.delay(durationMs);
        });
    }
    set(callback, durationMs = 1, persistent = false) {
        return this.timeouts.set(callback, durationMs, persistent);
    }
    setInterval(callback, durationMs = 1, persistent = false) {
        return this.timeouts.setInterval(callback, durationMs, persistent);
    }
}
export const timeouts = new GlobalTimeouts();
